import { useCallback, useRef, useState } from 'react';

import { enqueueSnackbar } from 'notistack';

import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';

import { PUBLIC_API_URL } from '../constants/config';
import { ensureValidToken, useLocalAuth } from '../context/localAuthContext';
import { requestAccessToken } from './useFetch';

const DEFAULT_API_PREFIX = '/api/v1';

export type SSEProgressEvent = {
  completed?: number;
  stage?: string;
  total?: number;
  type: 'progress';
};

type SSEResultEvent<T> = {
  created?: boolean;
  data: T;
  type: 'result';
};

type SSEErrorEvent = {
  message: string;
  type: 'error';
};

type SSEFieldEvent = {
  key: string;
  type: 'field';
  value: unknown;
};

type SSEFieldResetEvent = {
  type: 'field_reset';
};

type SSEEvent<T> =
  | SSEErrorEvent
  | SSEFieldEvent
  | SSEFieldResetEvent
  | SSEProgressEvent
  | SSEResultEvent<T>;

interface UseSSEMutationOptions {
  preferSSE?: boolean;
  url: string;
  urlPrefix?: string;
}

interface UseSSEMutationCallbacks<T> {
  messageFail?: null | string;
  messageSuccess?: null | string;
  onError?: (error: Error) => void;
  onField?: (key: string, value: unknown) => void;
  onFieldReset?: () => void;
  onProgress?: (event: SSEProgressEvent) => void;
  onSuccess?: (data: T) => void;
}

export interface UseSSEMutationResult<T, V> {
  abort: () => void;
  data: T | undefined;
  isPending: boolean;
  mutate: (variables: V) => void;
  mutateAsync: (variables: V) => Promise<T>;
  progress: null | SSEProgressEvent;
}

function deriveModelKey(url: string): string[] {
  const segments = url.replace(/^\//, '').split('/').filter(Boolean);
  if (segments.length >= 2 && segments[0] === 'admin') {
    return [segments[0], segments[1]];
  }
  return segments.length > 0 ? [segments[0]] : [];
}

function processSSEBlocks<T>(
  text: string,
  callbacksRef: React.MutableRefObject<undefined | UseSSEMutationCallbacks<T>>,
  setProgress: React.Dispatch<React.SetStateAction<null | SSEProgressEvent>>,
  unmountedRef: React.MutableRefObject<boolean>,
): { resultData?: T } {
  let resultData: T | undefined;
  const blocks = text.split('\n\n').filter(Boolean);

  for (const block of blocks) {
    const dataLine = block.split('\n').find((line) => line.startsWith('data: '));
    if (!dataLine) continue;

    const eventStr = dataLine.slice(6);
    let event: SSEEvent<T>;
    try {
      event = JSON.parse(eventStr);
    } catch {
      continue;
    }

    if (event.type === 'progress') {
      if (!unmountedRef.current) setProgress(event as SSEProgressEvent);
      callbacksRef.current?.onProgress?.(event as SSEProgressEvent);
    } else if (event.type === 'field') {
      const fieldEvent = event as SSEFieldEvent;
      callbacksRef.current?.onField?.(fieldEvent.key, fieldEvent.value);
    } else if (event.type === 'field_reset') {
      callbacksRef.current?.onFieldReset?.();
    } else if (event.type === 'result') {
      resultData = (event as SSEResultEvent<T>).data;
    } else if (event.type === 'error') {
      throw new Error((event as SSEErrorEvent).message);
    }
  }

  return { resultData };
}

function captureAbortTrace(): string {
  return new Error().stack || 'no stack';
}

export function useSSEMutation<T, V = any>(
  options: UseSSEMutationOptions,
  callbacks?: UseSSEMutationCallbacks<T>,
): UseSSEMutationResult<T, V> {
  const auth = useAuth0();
  const { token: localToken } = useLocalAuth();
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);
  const [progress, setProgress] = useState<null | SSEProgressEvent>(null);
  const [data, setData] = useState<T | undefined>(undefined);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  const abortRef = useRef<AbortController | null>(null);
  const unmountedRef = useRef(false);
  const requestIdRef = useRef(0);

  const prefix = options.urlPrefix ?? DEFAULT_API_PREFIX;
  const wantSSE = options.preferSSE !== false;

  const abort = useCallback(() => {
    if (abortRef.current) {
      console.warn(`[SSE ${options.url}] abort() called`, captureAbortTrace());
      abortRef.current.abort();
    }
    abortRef.current = null;
  }, [options.url]);

  const mutateAsync = useCallback(
    async (variables: V): Promise<T> => {
      const reqId = ++requestIdRef.current;
      const t0 = performance.now();
      console.info(`[SSE #${reqId} ${options.url}] mutateAsync started`);

      abort();
      const controller = new AbortController();
      abortRef.current = controller;
      unmountedRef.current = false;

      controller.signal.addEventListener('abort', () => {
        const elapsed = (performance.now() - t0).toFixed(0);
        console.error(
          `[SSE #${reqId} ${options.url}] signal aborted after ${elapsed}ms`,
          captureAbortTrace(),
        );
      });

      setIsPending(true);
      setProgress(null);
      setData(undefined);

      let authorization: string | undefined;
      if (localToken) {
        console.info(`[SSE #${reqId}] ensureValidToken start`);
        const validToken = await ensureValidToken();
        console.info(
          `[SSE #${reqId}] ensureValidToken done, hasToken=${!!validToken}, signalAborted=${controller.signal.aborted}`,
        );
        if (validToken) authorization = `Bearer ${validToken}`;
      } else {
        console.info(`[SSE #${reqId}] requestAccessToken start (Auth0)`);
        const resolved = await requestAccessToken(auth);
        console.info(
          `[SSE #${reqId}] requestAccessToken done, hasToken=${!!resolved}, signalAborted=${controller.signal.aborted}`,
        );
        if (resolved) authorization = `Bearer ${resolved}`;
      }

      if (controller.signal.aborted) {
        console.error(`[SSE #${reqId}] already aborted before fetch, skipping`);
        throw new DOMException('Aborted', 'AbortError');
      }

      try {
        console.info(`[SSE #${reqId}] fetch starting`);
        const response = await fetch(`${PUBLIC_API_URL}${prefix}${options.url}`, {
          body: JSON.stringify(variables),
          headers: {
            'Content-Type': 'application/json',
            ...(wantSSE && { Accept: 'text/event-stream' }),
            ...(authorization && { Authorization: authorization }),
          },
          method: 'POST',
          signal: controller.signal,
        });

        const elapsed = (performance.now() - t0).toFixed(0);
        console.info(`[SSE #${reqId}] response status=${response.status} after ${elapsed}ms`);

        if (!response.ok) {
          let errorMessage = response.statusText;
          try {
            const errorBody = await response.json();
            errorMessage = errorBody.error || errorBody.message || errorMessage;
          } catch {}
          throw new Error(errorMessage);
        }

        const contentType = response.headers.get('content-type') || '';

        if (!contentType.includes('text/event-stream')) {
          const jsonData = (await response.json()) as T;
          if (!unmountedRef.current) setData(jsonData);
          await queryClient.invalidateQueries({ queryKey: deriveModelKey(options.url) });
          if (callbacksRef.current?.messageSuccess) {
            enqueueSnackbar(callbacksRef.current.messageSuccess, {
              autoHideDuration: 3000,
              variant: 'success',
            });
          }
          callbacksRef.current?.onSuccess?.(jsonData);
          return jsonData;
        }

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let resultData: T | undefined;

        try {
          for (;;) {
            const { done, value } = await reader.read();
            if (done) {
              console.info(`[SSE #${reqId}] reader done, hasResult=${resultData !== undefined}`);
              break;
            }

            buffer += decoder.decode(value, { stream: true });

            const boundary = buffer.lastIndexOf('\n\n');
            if (boundary === -1) continue;

            const complete = buffer.slice(0, boundary + 2);
            buffer = buffer.slice(boundary + 2);

            const processed = processSSEBlocks<T>(
              complete,
              callbacksRef,
              setProgress,
              unmountedRef,
            );
            if (processed.resultData !== undefined) {
              resultData = processed.resultData;
            }
          }

          if (buffer.trim()) {
            const processed = processSSEBlocks<T>(buffer, callbacksRef, setProgress, unmountedRef);
            if (processed.resultData !== undefined) {
              resultData = processed.resultData;
            }
          }
        } finally {
          reader.releaseLock();
        }

        if (!resultData) {
          throw new Error('Stream ended without result');
        }

        if (!unmountedRef.current) setData(resultData);
        await queryClient.invalidateQueries({ queryKey: deriveModelKey(options.url) });
        if (callbacksRef.current?.messageSuccess) {
          enqueueSnackbar(callbacksRef.current.messageSuccess, {
            autoHideDuration: 3000,
            variant: 'success',
          });
        }
        callbacksRef.current?.onSuccess?.(resultData);
        return resultData;
      } catch (error: any) {
        const elapsed = (performance.now() - t0).toFixed(0);
        if (error?.name === 'AbortError') {
          console.error(`[SSE #${reqId}] AbortError after ${elapsed}ms`);
          throw error;
        }
        console.error(`[SSE #${reqId}] error after ${elapsed}ms:`, error?.message);
        const messageFail = callbacksRef.current?.messageFail;
        if (messageFail !== null) {
          const msg = messageFail || error.message || 'Request failed';
          enqueueSnackbar(msg, { autoHideDuration: 3000, variant: 'error' });
        }
        callbacksRef.current?.onError?.(error);
        throw error;
      } finally {
        if (!unmountedRef.current) setIsPending(false);
        abortRef.current = null;
      }
    },
    [abort, auth, localToken, options.url, prefix, wantSSE, queryClient],
  );

  const mutate = useCallback(
    (variables: V) => {
      mutateAsync(variables).catch(() => {});
    },
    [mutateAsync],
  );

  return { abort, data, isPending, mutate, mutateAsync, progress };
}
